import type { LucideIcon } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

export function AdminDashboardTabButton({
    active,
    icon: Icon,
    label,
    onClick,
}: {
    active: boolean;
    icon: LucideIcon;
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-950 transition-colors ${
                active ? 'border border-neutral-400 bg-white shadow-sm' : 'hover:bg-neutral-300/45'
            }`}
        >
            <Icon className="size-4" />
            {label}
        </button>
    );
}

export function AdminDashboardPaginatedSection<T>({
    rows,
    page,
    pageSize,
    total,
    totalPages,
    onPageChange,
    render,
}: {
    rows: T[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    render: (rows: T[]) => JSX.Element;
}) {
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const firstRow = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const lastRow = Math.min((safePage - 1) * pageSize + rows.length, total);

    return (
        <div className="space-y-3">
            {render(rows)}
            {total > pageSize && (
                <div className="flex items-center justify-between rounded-xl border bg-white px-4 py-3 text-sm text-neutral-600">
                    <span>
                        Showing {firstRow}-{lastRow} of {total}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={safePage === 1}
                            onClick={() => onPageChange(safePage - 1)}
                        >
                            Previous
                        </Button>
                        <span>
                            Page {safePage} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={safePage === totalPages}
                            onClick={() => onPageChange(safePage + 1)}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export function AdminDashboardEmptyState({ message }: { message: string }) {
    return (
        <Card>
            <CardContent className="p-10 text-center text-sm text-neutral-500">
                {message}
            </CardContent>
        </Card>
    );
}
